import { AgencyQuickReplyDto } from '@bandido/shared/dtos';
import {
  Box,
  CircularProgress,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  getQuickReplyOptionId,
  WHATSAPP_QUICK_REPLIES_COMMAND_OPTION_ID,
} from '../WhatsappClient/slash-command';

type SlashPaletteStage = 'commands' | 'quickReplies';

type WhatsappSlashCommandPaletteProps = {
  open: boolean;
  stage: SlashPaletteStage;
  commandAliases: string[];
  showQuickRepliesCommand: boolean;
  quickReplies: AgencyQuickReplyDto[];
  isLoadingQuickReplies: boolean;
  isRenderingQuickReply: boolean;
  hasCaseContext: boolean;
  activeIndex: number;
  listboxId: string;
  onSelectQuickRepliesCommand: () => void;
  onSelectQuickReply: (quickReply: AgencyQuickReplyDto) => void;
};

const getBodyPreview = (body: string, maxLength = 80) => {
  const normalizedBody = body.replace(/\s+/g, ' ').trim();
  if (normalizedBody.length <= maxLength) {
    return normalizedBody;
  }

  return `${normalizedBody.slice(0, maxLength)}...`;
};

export const WhatsappSlashCommandPalette = ({
  open,
  stage,
  commandAliases,
  showQuickRepliesCommand,
  quickReplies,
  isLoadingQuickReplies,
  isRenderingQuickReply,
  hasCaseContext,
  activeIndex,
  listboxId,
  onSelectQuickRepliesCommand,
  onSelectQuickReply,
}: WhatsappSlashCommandPaletteProps) => {
  const { t } = useTranslation();

  if (!open) {
    return null;
  }

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 'calc(100% + 8px)',
        zIndex: 10,
        maxHeight: 360,
        overflow: 'hidden',
        borderRadius: 2,
      }}
    >
      {stage === 'commands' ? (
        <List
          id={listboxId}
          dense
          disablePadding
          role="listbox"
          aria-label={t('whatsapp.slashCommands.commands.quickReplies.label')}
        >
          {!showQuickRepliesCommand ? (
            <Box px={2} py={1.5}>
              <Typography variant="body2" color="text.secondary">
                {t('whatsapp.slashCommands.emptyCommands')}
              </Typography>
            </Box>
          ) : (
            <ListItemButton
              id={WHATSAPP_QUICK_REPLIES_COMMAND_OPTION_ID}
              onClick={onSelectQuickRepliesCommand}
              selected={activeIndex === 0}
              role="option"
              aria-selected={activeIndex === 0}
            >
              <ListItemText
                primary={t('whatsapp.slashCommands.commands.quickReplies.label')}
                secondary={
                  <Box display="flex" flexDirection="column" gap={0.25}>
                    <Typography variant="body2" color="text.secondary">
                      {t('whatsapp.slashCommands.commands.quickReplies.description')}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {commandAliases.join('  •  ')}
                    </Typography>
                  </Box>
                }
              />
            </ListItemButton>
          )}
        </List>
      ) : (
        <Box display="flex" flexDirection="column" gap={1} p={1}>
          <Typography variant="subtitle2" px={1}>
            {t('whatsapp.slashCommands.commands.quickReplies.label')}
          </Typography>

          {!hasCaseContext ? (
            <Box px={1} pb={1}>
              <Typography variant="body2" color="text.secondary">
                {t('whatsapp.slashCommands.quickReplies.requiresCase')}
              </Typography>
            </Box>
          ) : (
            <>
              {isLoadingQuickReplies ? (
                <Box
                  display="flex"
                  alignItems="center"
                  gap={1}
                  px={1}
                  py={2}
                >
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    {t('whatsapp.slashCommands.quickReplies.loading')}
                  </Typography>
                </Box>
              ) : quickReplies.length === 0 ? (
                <Box px={1} py={2}>
                  <Typography variant="body2" color="text.secondary">
                    {t('whatsapp.slashCommands.quickReplies.empty')}
                  </Typography>
                </Box>
              ) : (
                <List
                  id={listboxId}
                  dense
                  disablePadding
                  role="listbox"
                  aria-label={t('whatsapp.slashCommands.commands.quickReplies.label')}
                  sx={{ maxHeight: 260, overflowY: 'auto' }}
                >
                  {quickReplies.map((quickReply, index) => (
                    <ListItemButton
                      id={getQuickReplyOptionId(quickReply.id)}
                      key={quickReply.id}
                      onClick={() => onSelectQuickReply(quickReply)}
                      disabled={isRenderingQuickReply}
                      alignItems="flex-start"
                      selected={activeIndex === index}
                      role="option"
                      aria-selected={activeIndex === index}
                    >
                      <ListItemText
                        primary={`${quickReply.slug} · ${quickReply.name}`}
                        secondary={getBodyPreview(quickReply.body)}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};
